import { useState, useEffect, useRef, useCallback } from 'react';

// Recording states
export const RECORDING_STATES = {
  IDLE: 'idle',
  STARTING: 'starting',
  RECORDING: 'recording',
  STOPPING: 'stopping',
  PROCESSING: 'processing',
  ERROR: 'error'
};

// Audio processing modes - now only supports backend STT
export const AUDIO_MODES = {
  BACKEND_STT: 'backend_stt'
};

/**
 * Enhanced audio recording hook with Google STT backend support only
 * 
 * @param {Object} config - Configuration object
 * @param {string} config.mode - Audio processing mode (only BACKEND_STT supported)
 * @param {function} config.onTranscript - Callback for final transcripts
 * @param {function} config.onLiveCaption - Callback for live captions
 * @param {function} config.onAudioChunk - Callback for raw audio chunks
 * @param {function} config.onStreamingTranscript - Callback for streaming interim transcripts
 * @param {function} config.onError - Error callback
 * @param {function} config.onVADEvent - Callback for VAD events (speaking/silence/auto-close)
 * @param {Object} config.audioConstraints - Audio constraints for getUserMedia
 * @param {Object} config.backendConfig - Backend-specific configuration
 * @param {Object} config.vadConfig - VAD configuration for automatic mic closure
 */
export const useEnhancedAudioRecording = ({
  mode = AUDIO_MODES.BACKEND_STT,
  onTranscript,
  onLiveCaption,
  onAudioChunk,
  onStreamingTranscript,
  onError,
  onVADEvent,
  audioConstraints = {
    sampleRate: 16000,
    channelCount: 1,
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true
  },
  backendConfig = {
    chunkDurationMs: 2000,
    enableSpeechDetection: true,
    qualityMetrics: true
  },
  vadConfig = {
    silenceThreshold: 3.0,      // 3 seconds of silence triggers auto-close
    maxSpeakingTime: 60.0,      // 60 seconds max recording time
    warningDelay: 1.0,          // 1 second warning before auto-close
    enableAutoClose: true       // Enable automatic mic closure
  }
}) => {
  // State management
  const [recordingState, setRecordingState] = useState(RECORDING_STATES.IDLE);
  const [isPermissionGranted, setIsPermissionGranted] = useState(false);
  const [currentTranscript, setCurrentTranscript] = useState('');
  const [liveCaption, setLiveCaption] = useState('');
  const [streamingTranscript, setStreamingTranscript] = useState('');
  const [lastError, setLastError] = useState(null);
  const [audioQualityMetrics, setAudioQualityMetrics] = useState(null);

  // Refs for persistent values
  const audioContextRef = useRef(null);
  const audioWorkletNodeRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const recordingStateRef = useRef(RECORDING_STATES.IDLE);
  const sequenceNumberRef = useRef(0);
  const isManualStopRef = useRef(false);
  const lastOperationTimeRef = useRef(0);
  const isUnmountingRef = useRef(false);
  const stopRecordingRef = useRef(null);

  // VAD (Voice Activity Detection) refs and state
  const vadState = useRef({
    isSpeaking: false,
    lastSpeechTime: Date.now(),
    silenceDuration: 0,
    speakingDuration: 0,
    recordingStartTime: null,
    silenceTimer: null,
    autoCloseTimer: null,
    maxTimeTimer: null
  });
  const [vadStatus, setVadStatus] = useState('idle'); // 'idle', 'speaking', 'silence', 'warning'

  // Update ref whenever recording state changes
  useEffect(() => {
    recordingStateRef.current = recordingState;
  }, [recordingState]);

  // Update stopRecording ref to avoid circular dependencies

  // Request microphone permission
  const requestPermission = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints
      });
      setIsPermissionGranted(true);

      // Stop the test stream
      stream.getTracks().forEach(track => track.stop());

      return true;
    } catch (error) {
      // console.error('❌ Microphone permission denied:', error);
      setLastError(error);
      setIsPermissionGranted(false);
      onError?.(error);
      return false;
    }
  }, [audioConstraints, onError]);

  // VAD Helper Functions
  const clearVadTimers = useCallback(() => {
    if (vadState.current.silenceTimer) {
      clearTimeout(vadState.current.silenceTimer);
      vadState.current.silenceTimer = null;
    }
    if (vadState.current.autoCloseTimer) {
      clearTimeout(vadState.current.autoCloseTimer);
      vadState.current.autoCloseTimer = null;
    }
    if (vadState.current.maxTimeTimer) {
      clearTimeout(vadState.current.maxTimeTimer);
      vadState.current.maxTimeTimer = null;
    }
  }, []);

  const resetVadState = useCallback(() => {
    clearVadTimers();
    vadState.current = {
      isSpeaking: false,
      lastSpeechTime: Date.now(),
      silenceDuration: 0,
      speakingDuration: 0,
      recordingStartTime: Date.now(),
      silenceTimer: null,
      autoCloseTimer: null,
      maxTimeTimer: null
    };
    setVadStatus('idle');
  }, [clearVadTimers]);

  const handleSpeechActivity = useCallback((hasSpeech) => {
    console.log('🎙️ VAD: handleSpeechActivity called with:', { hasSpeech, enableAutoClose: vadConfig.enableAutoClose, recordingState: recordingStateRef.current });

    if (!vadConfig.enableAutoClose || recordingStateRef.current !== RECORDING_STATES.RECORDING) {
      return;
    }

    const now = Date.now();
    const currentVad = vadState.current;

    if (hasSpeech) {
      // Speech detected - reset silence tracking
      if (!currentVad.isSpeaking) {
        console.log('🎤 Speech activity detected');
        currentVad.isSpeaking = true;
        currentVad.lastSpeechTime = now;
        currentVad.silenceDuration = 0;
        setVadStatus('speaking');

        // Clear any pending auto-close timers
        clearVadTimers();
      }

      // Update speaking duration
      if (currentVad.recordingStartTime) {
        currentVad.speakingDuration = (now - currentVad.recordingStartTime) / 1000;

        // Check for max speaking time
        if (currentVad.speakingDuration > vadConfig.maxSpeakingTime) {
          console.log('⏰ Max speaking time reached, auto-closing mic');
          onVADEvent?.({
            type: 'auto_close',
            reason: 'max_speaking_time',
            duration: currentVad.speakingDuration
          });
          stopRecordingRef.current?.('timeout - max speaking time reached');
          return;
        }
      }
    } else {
      // No speech detected - start silence tracking
      if (currentVad.isSpeaking) {
        console.log('🔇 Silence detected, starting silence timer');
        currentVad.isSpeaking = false;
        setVadStatus('silence');

        // Start silence timer
        currentVad.silenceTimer = setTimeout(() => {
          const silenceTime = (Date.now() - currentVad.lastSpeechTime) / 1000;
          currentVad.silenceDuration = silenceTime;

          if (silenceTime >= vadConfig.silenceThreshold) {
            console.log(`🔕 Silence threshold (${vadConfig.silenceThreshold}s) reached, scheduling auto-close`);
            setVadStatus('warning');

            // Show warning and schedule auto-close
            currentVad.autoCloseTimer = setTimeout(() => {
              if (recordingStateRef.current === RECORDING_STATES.RECORDING) {
                console.log('🛑 Auto-closing mic due to silence');
                onVADEvent?.({
                  type: 'auto_close',
                  reason: 'silence_detected',
                  silenceDuration: currentVad.silenceDuration
                });
                stopRecordingRef.current?.('auto-close - silence detected');
              }
            }, vadConfig.warningDelay * 1000);
          }
        }, vadConfig.silenceThreshold * 1000);
      }
    }
  }, [vadConfig, clearVadTimers, onVADEvent]);

  // Initialize AudioContext and AudioWorklet for raw PCM capture
  const initializeAudioWorklet = useCallback(async () => {
    try {
      console.log('🎤 Initializing AudioWorklet for Google STT...');

      // Create new AudioContext
      const audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: audioConstraints.sampleRate || 16000,
        latencyHint: 'interactive'
      });

      // CRITICAL FIX: Resume AudioContext if suspended (required by modern browsers)
      if (audioContext.state === 'suspended') {
        console.log('🔄 AudioContext suspended, resuming...');
        await audioContext.resume();
        console.log('✅ AudioContext resumed, state:', audioContext.state);
      }

      console.log('🎤 AudioContext state:', audioContext.state, 'Sample rate:', audioContext.sampleRate);

      // Get user media
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints
      });

      // DEBUGGING: Check if stream has active audio tracks
      const audioTracks = stream.getAudioTracks();
      console.log('🎤 Audio tracks:', audioTracks.length);
      audioTracks.forEach((track, index) => {
        console.log(`🎤 Track ${index}:`, {
          label: track.label,
          enabled: track.enabled,
          muted: track.muted,
          readyState: track.readyState,
          settings: track.getSettings()
        });
      });

      mediaStreamRef.current = stream;
      audioContextRef.current = audioContext;

      // Add the audio capture processor worklet
      await audioContext.audioWorklet.addModule('/audio-capture-processor.js');

      // Create source from media stream
      const source = audioContext.createMediaStreamSource(stream);

      // DEBUGGING: Add audio level monitoring to verify microphone input
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);

      // Monitor audio levels for 3 seconds to verify microphone is working
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      let maxLevel = 0;
      let sampleCount = 0;
      const monitorStart = Date.now();

      const monitorAudio = () => {
        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        maxLevel = Math.max(maxLevel, average);
        sampleCount++;

        const elapsed = Date.now() - monitorStart;
        if (elapsed < 3000) {
          setTimeout(monitorAudio, 100);
        } else {
          console.log(`🎤 Audio monitoring complete: Max level: ${maxLevel.toFixed(1)}, Samples: ${sampleCount}`);
          if (maxLevel < 5) {
            console.warn('⚠️ Very low audio levels detected - microphone might be muted or not working properly');
          } else {
            console.log('✅ Good audio levels detected - microphone is working');
          }
        }
      };
      monitorAudio();

      // Create AudioWorklet node
      const audioWorkletNode = new AudioWorkletNode(audioContext, 'audio-capture-processor', {
        processorOptions: {
          chunkDurationMs: backendConfig.chunkDurationMs,
          enableSpeechDetection: backendConfig.enableSpeechDetection,
          qualityMetrics: backendConfig.qualityMetrics
        }
      });

      // Set up message handling from AudioWorklet
      audioWorkletNode.port.onmessage = (event) => {
        const { type, data } = event.data;

        if (type === 'AUDIO_CHUNK') {
          // Send audio chunk to parent for backend processing
          onAudioChunk?.(data);
          sequenceNumberRef.current++;

          // Debug: Log speech detection data
          console.log('🎙️ VAD Debug: Audio chunk received:', {
            hasSpeech: data.chunk_info?.has_speech,
            vadEnabled: vadConfig.enableAutoClose,
            recordingState: recordingStateRef.current
          });

          // Handle VAD from audio chunk data
          if (vadConfig.enableAutoClose && data.chunk_info?.has_speech !== undefined) {
            handleSpeechActivity(data.chunk_info.has_speech);
          }
        } else if (type === 'QUALITY_METRICS') {
          setAudioQualityMetrics(data);
        }
      };

      // Connect audio graph
      source.connect(audioWorkletNode);

      audioWorkletNodeRef.current = audioWorkletNode;

      console.log('✅ AudioWorklet initialized successfully');
      return true;
    } catch (error) {
      console.error('❌ Failed to initialize AudioWorklet:', error);
      setLastError(error);
      onError?.(error);
      throw error;
    }
  }, [audioConstraints, onAudioChunk, backendConfig, onError, vadConfig, handleSpeechActivity]);

  // Start recording with Google STT backend only
  const startRecording = useCallback(async () => {
    // Prevent rapid start/stop cycles
    const now = Date.now();
    const timeSinceLastOperation = now - lastOperationTimeRef.current;
    if (timeSinceLastOperation < 300) {
      console.warn(`⚠️ Preventing rapid operation - only ${timeSinceLastOperation}ms since last operation`);
      return false;
    }

    // Prevent multiple simultaneous starts
    if (recordingStateRef.current !== RECORDING_STATES.IDLE) {
      console.warn('⚠️ Recording already in progress or starting:', recordingStateRef.current);
      return false;
    }

    lastOperationTimeRef.current = now;

    setRecordingState(RECORDING_STATES.STARTING);
    setLastError(null);
    setCurrentTranscript('');
    setLiveCaption('');
    setStreamingTranscript('');
    isManualStopRef.current = false;
    sequenceNumberRef.current = 0;

    // Initialize VAD state
    resetVadState();

    try {
      console.log(`🎤 Starting audio recording in ${mode} mode`);
      console.log('🎤 Initializing Google STT backend service (AudioWorklet only)...');

      await initializeAudioWorklet();

      // Send start recording message to AudioWorklet
      if (audioWorkletNodeRef.current) {
        audioWorkletNodeRef.current.port.postMessage({
          type: 'START_RECORDING'
        });
        console.log('✅ Google STT AudioWorklet initialized and started');
      } else {
        throw new Error('Failed to initialize AudioWorklet for Google STT');
      }

      setRecordingState(RECORDING_STATES.RECORDING);

      // Set up max recording time timer
      if (vadConfig.enableAutoClose && vadConfig.maxSpeakingTime > 0) {
        vadState.current.maxTimeTimer = setTimeout(() => {
          if (recordingStateRef.current === RECORDING_STATES.RECORDING) {
            console.log(`⏰ Max recording time (${vadConfig.maxSpeakingTime}s) reached`);
            onVADEvent?.({
              type: 'auto_close',
              reason: 'max_time_limit',
              duration: vadConfig.maxSpeakingTime
            });
            stopRecordingRef.current?.('auto-close - max time limit reached');
          }
        }, vadConfig.maxSpeakingTime * 1000);
      }

      console.log('✅ Audio recording initialization completed successfully');
      return true;
    } catch (error) {
      console.error('❌ Failed to start recording:', error);
      setLastError(error);
      setRecordingState(RECORDING_STATES.ERROR);
      onError?.(error);
      return false;
    }
  }, [mode, initializeAudioWorklet, onError, resetVadState, vadConfig, onVADEvent]);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (recordingStateRef.current === RECORDING_STATES.IDLE) {
      console.debug('ℹ️ Stop recording called but recording not active');
      return;
    }

    // Prevent rapid operations (but allow during unmount)
    if (!isUnmountingRef.current) {
      const now = Date.now();
      const timeSinceLastOperation = now - lastOperationTimeRef.current;
      if (timeSinceLastOperation < 100) {
        console.warn(`⚠️ Preventing rapid stop - only ${timeSinceLastOperation}ms since last operation`);
        return;
      }
      lastOperationTimeRef.current = now;
    }

    console.log('🛑 Stopping audio recording - Current state:', recordingStateRef.current);

    isManualStopRef.current = true;
    setRecordingState(RECORDING_STATES.STOPPING);

    try {
      // Clean up VAD timers
      clearVadTimers();
      setVadStatus('idle');

      // Stop AudioWorklet recording
      if (audioWorkletNodeRef.current) {
        audioWorkletNodeRef.current.port.postMessage({
          type: 'STOP_RECORDING'
        });

        // Disconnect audio graph
        audioWorkletNodeRef.current.disconnect();
        audioWorkletNodeRef.current = null;
      }

      // Stop AudioContext
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }

      // Stop media stream
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
        mediaStreamRef.current = null;
      }

      setLiveCaption('');
      setStreamingTranscript('');
      setRecordingState(RECORDING_STATES.IDLE);
      console.log('✅ Recording stopped successfully');

    } catch (error) {
      console.error('❌ Error stopping recording:', error);
      setLastError(error);
      setRecordingState(RECORDING_STATES.ERROR);
      onError?.(error);
    }
  }, [onError, clearVadTimers]);

  // Update stopRecording ref to avoid circular dependencies
  useEffect(() => {
    stopRecordingRef.current = stopRecording;
  }, [stopRecording]);

  // Suspend recording (pause without stopping completely)
  const suspendRecording = useCallback(() => {
    if (recordingStateRef.current === RECORDING_STATES.RECORDING) {
      console.log('⏸️ Suspending audio recording');

      if (audioWorkletNodeRef.current) {
        audioWorkletNodeRef.current.port.postMessage({
          type: 'SUSPEND_RECORDING'
        });
      }
    }
  }, []);

  // Resume recording from suspended state
  const resumeRecording = useCallback(() => {
    if (recordingStateRef.current === RECORDING_STATES.RECORDING) {
      console.log('▶️ Resuming audio recording');

      if (audioWorkletNodeRef.current) {
        audioWorkletNodeRef.current.port.postMessage({
          type: 'RESUME_RECORDING'
        });
      } else {
        // If no AudioWorklet, restart recording
        startRecording();
      }
    }
  }, [startRecording]);

  // Update configuration
  const updateConfig = useCallback((newConfig) => {
    console.log('🔧 Updating audio recording configuration:', newConfig);

    if (audioWorkletNodeRef.current) {
      audioWorkletNodeRef.current.port.postMessage({
        type: 'UPDATE_CONFIG',
        config: newConfig
      });
    }
  }, []);

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      console.log('🧹 Enhanced audio recording hook cleanup - Current state:', recordingStateRef.current);
      isUnmountingRef.current = true;

      if (recordingStateRef.current !== RECORDING_STATES.IDLE) {
        stopRecordingRef.current?.();
      }
    };
  }, []); // Empty dependency array to run only on mount/unmount

  // Computed states
  const isRecording = recordingState === RECORDING_STATES.RECORDING;
  const isStarting = recordingState === RECORDING_STATES.STARTING;
  const isStopping = recordingState === RECORDING_STATES.STOPPING;
  const hasError = recordingState === RECORDING_STATES.ERROR;
  const isAudioWorkletSupported = !!(window.AudioWorklet && window.AudioContext);

  return {
    recordingState,
    isRecording,
    isStarting,
    isStopping,
    hasError,
    isPermissionGranted,
    currentTranscript,
    liveCaption,
    streamingTranscript,
    lastError,
    audioQualityMetrics,
    isAudioWorkletSupported,
    requestPermission,
    startRecording,
    stopRecording,
    suspendRecording,
    resumeRecording,
    updateConfig,
    sequenceNumber: sequenceNumberRef.current,
    // VAD status and functions
    vadStatus,
    vadState: vadState.current
  };
};

export default useEnhancedAudioRecording;
